import { apiGet, apiPost, apiPut } from './api';
import type { Plan } from './plans';
import type { ShareVisibility } from '@utils/shareVisibility';

export interface PlanInvite {
	id: number;
	code: string;
	company_id: number;
	company_name?: string;
	invited_email?: string | null;
	invited_phone?: string | null;
	payment_model: 'company_pays' | 'seeker_pays' | 'free';
	status: 'pending' | 'used' | 'expired' | 'cancelled';
	expires_at?: string | null;
	used_at?: string | null;
	used_by_user_id?: number | null;
	plan_id?: number | null;
	share_visibility?: ShareVisibility;
}

export interface PlanInviteDetailsResponse {
	invite: {
		id: number;
		code: string;
		status: 'pending' | 'used' | 'expired' | 'cancelled';
		payment_model: 'company_pays' | 'seeker_pays' | 'free';
		expires_at: string | null;
		company_id: number;
		plan_id: number | null;
		share_visibility?: ShareVisibility;
	};
	company: {
		id: number;
		name: string;
	};
}

/** Slim summary of invites that are relevant for a logged-in seeker */
export interface SeekerPlanInviteSummary {
	id: number;
	code: string;
	status: PlanInvite['status'];
	payment_model: PlanInvite['payment_model'];
	expires_at: string | null;
	company_id: number;
	plan_id?: number | null;
	company_name: string;
}

export interface SeekerDirectPlan {
	id: number;
	user_id: number;
	title: string;
	description?: string;
	style?: string;
	location?: string;
	status: 'draft' | 'published' | 'closed' | 'archived';
	before_image_url?: string;
	after_image_url?: string;
	ai_plan_details?: any;
	created_at: string;
	updated_at: string;
	// Invite linkage metadata
	invite_id?: number;
	invite_code?: string;
	invite_status?: 'pending' | 'used' | 'expired' | 'cancelled';
	company_name?: string;
	seeker_can_edit?: number | boolean;
	share_visibility?: ShareVisibility;
	// Optional questionnaire fields
	garden_size?: number | null;
	garden_shape?: string | null;
	garden_components?: any;
	needs_wants?: string | null;
	maintenance_level?: string | null;
	children_pets?: string | null;
	budget_min?: number | null;
	budget_max?: number | null;
	timeline?: string | null;
	project_start_date?: string | null;
}

export interface PlanInviteForSeeker {
	has_invite: boolean;
	invite_id?: number;
	plan_id?: number;
	code?: string;
	status?: 'pending' | 'used' | 'expired' | 'cancelled';
	accepted?: boolean;
	seeker_can_edit?: boolean;
	company_id?: number;
	company_name?: string;
	share_visibility?: ShareVisibility;
}

export interface CompanyPlanInviteStatus {
	has_invite: boolean;
	invite_id?: number;
	plan_id?: number;
	company_id?: number;
	code?: string;
	status?: 'pending' | 'used' | 'expired' | 'cancelled';
	invited_email?: string | null;
	allow_client_edit?: boolean;
	used_by_user_id?: number | null;
	linked_user_name?: string | null;
	linked_user_email?: string | null;
	expires_at?: string | null;
	share_visibility?: ShareVisibility;
}

/**
 * Create a new plan invite for the authenticated company user.
 * The backend uses userId (body) to determine which company to attach it to.
 */
export async function createPlanInvite(payload: {
	userId: number;
	companyId?: number;
	invitedEmail?: string;
	invitedPhone?: string;
	paymentModel?: 'company_pays' | 'seeker_pays' | 'free';
	expiresAt?: string;
	shareVisibility?: ShareVisibility;
}): Promise<PlanInvite> {
	const body: any = {
		userId: payload.userId,
		invitedEmail: payload.invitedEmail,
		invitedPhone: payload.invitedPhone,
		paymentModel: payload.paymentModel,
		expiresAt: payload.expiresAt,
		shareVisibility: payload.shareVisibility
	};
	if (payload.companyId != null) {
		body.companyId = payload.companyId;
	}
	return apiPost<PlanInvite>('/plan-invites', body);
}

/**
 * Lookup invite details by code (used by seekers arriving via a link).
 */
export async function getPlanInviteByCode(code: string): Promise<PlanInviteDetailsResponse> {
	return apiGet<PlanInviteDetailsResponse>(`/plan-invites?code=${encodeURIComponent(code)}`);
}

/**
 * Get plan invites for the currently authenticated seeker (matched by email).
 * Used to show a persistent "you've been invited" banner on the My Plans page.
 */
export async function getMySeekerPlanInvites(userId: number): Promise<SeekerPlanInviteSummary[]> {
	// userId is passed only for auth consistency/logging on the backend; matching is done by email.
	const params = new URLSearchParams({ userId: String(userId) });
	return apiGet<SeekerPlanInviteSummary[]>(`/users/me/plan-invites?${params.toString()}`);
}

/**
 * Decline a plan invite so it can no longer be used.
 * userId is required by the backend for auth.
 */
export async function declinePlanInvite(inviteId: number, userId: number): Promise<{ id: number; status: 'cancelled'; company_name: string; code: string }> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiPost<{ id: number; status: 'cancelled'; company_name: string; code: string }>(
		`/plan-invites/${inviteId}/decline?${params.toString()}`,
		{}
	);
}

/**
 * Claim an invite for the current seeker using a code.
 * This links the invite to their user ID while keeping it pending.
 */
export async function claimPlanInvite(userId: number, code: string): Promise<SeekerPlanInviteSummary> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiPost<SeekerPlanInviteSummary>(
		`/plan-invites/claim?${params.toString()}`,
		{ code }
	);
}

/**
 * List plans that are exclusive to the authenticated company (direct plans from invites).
 */
export async function getCompanyInvitePlans(userId: number, status?: 'draft' | 'published' | 'closed' | 'archived'): Promise<Plan[]> {
	const params = new URLSearchParams({ userId: String(userId) });
	if (status) {
		params.set('status', status);
	}
	return apiGet<Plan[]>(`/companies/my/invite-plans?${params.toString()}`);
}

/**
 * Invite a specific client to an existing plan/draft (no code entry needed in seeker UI).
 */
export async function inviteClientToPlan(payload: {
	planId: number;
	userId: number;
	invitedEmail?: string;
	allowEdit?: boolean;
	shareVisibility?: ShareVisibility;
}): Promise<{
	invite_id: number;
	code?: string;
	plan_id: number;
	company_id: number;
	invited_email: string | null;
	linked_user_id: number | null;
	allow_edit: boolean;
	share_visibility?: ShareVisibility;
}> {
	return apiPost(`/plans/${payload.planId}/invite-client`, {
		userId: payload.userId,
		invitedEmail: payload.invitedEmail,
		allowEdit: payload.allowEdit ?? true,
		shareVisibility: payload.shareVisibility
	});
}

/**
 * List direct plans shared with currently authenticated seeker.
 */
export async function getMySeekerDirectPlans(userId: number): Promise<SeekerDirectPlan[]> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiGet<SeekerDirectPlan[]>(`/users/me/direct-plans?${params.toString()}`);
}

/**
 * Get current seeker's invite state for a specific plan.
 */
export async function getMyInviteForPlan(planId: number, userId: number): Promise<PlanInviteForSeeker> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiGet<PlanInviteForSeeker>(`/plans/${planId}/my-invite?${params.toString()}`);
}

export async function acceptPlanInviteForPlan(planId: number, userId: number): Promise<PlanInviteForSeeker> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiPost<PlanInviteForSeeker>(`/plans/${planId}/accept-invite?${params.toString()}`, {});
}

export async function getCompanyInviteForPlan(planId: number, userId: number): Promise<CompanyPlanInviteStatus> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiGet<CompanyPlanInviteStatus>(`/plans/${planId}/company-invite?${params.toString()}`);
}

export async function cancelCompanyInviteForPlan(planId: number, userId: number): Promise<{ invite_id: number; status: 'cancelled' }> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiPost<{ invite_id: number; status: 'cancelled' }>(`/plans/${planId}/company-invite/cancel?${params.toString()}`, {});
}

export async function updateCompanyInviteVisibilityForPlan(
	planId: number,
	userId: number,
	shareVisibility: ShareVisibility
): Promise<{
	invite_id: number;
	plan_id: number;
	status: string;
	share_visibility: ShareVisibility;
}> {
	const params = new URLSearchParams({ userId: String(userId) });
	return apiPut(`/plans/${planId}/company-invite/visibility?${params.toString()}`, {
		shareVisibility
	});
}

/** Admin-only: plan with seeker and company info (from GET /admin/direct-plans) */
export interface AdminDirectPlan extends Plan {
	seeker_name?: string | null;
	seeker_email?: string | null;
	company_name?: string | null;
}

/**
 * Admin only: list all direct plans (any company). Pass current user id for auth. Optional search by seeker name/email or company name.
 */
export async function getAdminDirectPlans(userId: string | number, search?: string): Promise<AdminDirectPlan[]> {
	const params = new URLSearchParams({ userId: String(userId) });
	if (search && search.trim()) {
		params.set('search', search.trim());
	}
	return apiGet<AdminDirectPlan[]>(`/admin/direct-plans?${params.toString()}`);
}

/** One suggestion item for the direct-plans filter dropdown */
export interface DirectPlansSuggestion {
	type: 'seeker' | 'company' | 'company_user';
	id: number;
	label: string;
	sublabel?: string | null;
	searchValue: string;
}

/**
 * Admin only: get autocomplete suggestions for the direct-plans filter (seekers, companies, company users).
 */
export async function getAdminDirectPlansSuggest(userId: string | number, q: string): Promise<{ suggestions: DirectPlansSuggestion[] }> {
	if (!q || q.trim().length < 2) return { suggestions: [] };
	const params = new URLSearchParams({ userId: String(userId), q: q.trim() });
	return apiGet<{ suggestions: DirectPlansSuggestion[] }>(`/admin/direct-plans/suggest?${params.toString()}`);
}

